import React, { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { MarkdownEditor } from '@/components/markdown/MarkdownEditor';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useDeleteFile, useUploadFile } from '@/hooks/useFiles';
import { Attribute } from '@/types';
import { cn } from '@/utils/classname';

import {
  H1ReportSource,
  H1SeverityRating,
  useHackerOneActivities,
  useUpdateHackerOneSeverity,
} from './HackerOneReportPanel.queries';

interface AttachmentState {
  localId: string;
  name: string;
  size: number;
  status: 'uploading' | 'complete' | 'error';
  progress: number;
  s3Key?: string;
  error?: string;
}

const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024; // 50 MiB, matches backend cap

function sanitizeFileName(name: string): string {
  return name.replace(/[^A-Za-z0-9._-]/g, '_');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * HackerOne report attributes stored on a risk
 * These are extracted from risk.attributes where the attribute name
 * matches the hackerone-report-* pattern
 */
interface H1ReportAttributes {
  reportId?: string;
  status?: string;
  program?: string;
  source?: H1ReportSource;
}

/**
 * Extract HackerOne report attributes from a risk's attribute array
 */
function extractH1Attributes(attributes: Attribute[]): H1ReportAttributes {
  const result: H1ReportAttributes = {};
  for (const attr of attributes) {
    if (attr.name === 'hackerone-report-id') {
      result.reportId = attr.value;
    } else if (attr.name === 'hackerone-status') {
      result.status = attr.value;
    } else if (attr.name === 'hackerone-program') {
      result.program = attr.value;
    } else if (attr.name === 'hackerone-source') {
      if (attr.value === 'hacker-api' || attr.value === 'org-api') {
        result.source = attr.value;
      } else {
        console.warn(
          `[HackerOneReportPanel] Unknown hackerone-source value "${attr.value}", defaulting to "hacker-api"`
        );
        result.source = 'hacker-api';
      }
    }
  }
  return result;
}

/**
 * Strip the leading "activity-" prefix and title-case the remainder.
 * e.g. "activity-comment" → "Comment"
 *      "activity-bug-resolved" → "Bug-resolved"
 */
function formatActivityType(type: string): string {
  const stripped = type.replace(/^activity-/i, '');
  if (!stripped) return type;
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

/**
 * Status badge styling for HackerOne report statuses
 */
function getStatusBadgeClass(status: string): string {
  const lower = status.toLowerCase();
  if (lower === 'triaged' || lower === 'resolved') {
    return 'bg-chariot-status-positive/10 text-chariot-status-positive';
  }
  if (lower === 'new' || lower === 'pending') {
    return 'bg-chariot-status-medium/10 text-chariot-status-medium';
  }
  if (
    lower === 'duplicate' ||
    lower === 'informative' ||
    lower === 'not-applicable'
  ) {
    return 'bg-chariot-text-secondary/10 text-chariot-text-secondary';
  }
  return 'bg-chariot-layer-primary/10 text-chariot-layer-primary';
}

export interface HackerOneReportPanelProps {
  /** Fetched attribute records for this risk (passed in from the parent to avoid re-fetching) */
  attributes: Attribute[];
  /** Optional HackerOne program URL for linking */
  programUrl?: string;
  /** Callback to send a reply to the report */
  onReply?: (
    reportId: string,
    source: H1ReportSource,
    message: string,
    internal: boolean,
    attachmentS3Keys?: string[]
  ) => Promise<void>;
}

/**
 * HackerOneReportPanel
 *
 * Displays the status, activities, and reply form for a HackerOne report
 * associated with a vulnerability risk.
 *
 * Extracts data from the provided attributes matching:
 * - hackerone-report-id: The report ID
 * - hackerone-status: Current report status
 * - hackerone-program: Program handle
 * - hackerone-source: API source used to submit (hacker-api | org-api)
 *
 * Activities are fetched live from GET /hackerone/activities.
 */
export const HackerOneReportPanel: React.FC<HackerOneReportPanelProps> = ({
  attributes,
  programUrl,
  onReply,
}) => {
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);
  const [dropZoneError, setDropZoneError] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Severity update state — only used when resolvedSource === 'org-api'
  const [selectedRating, setSelectedRating] = useState<H1SeverityRating | ''>(
    ''
  );
  const [severityError, setSeverityError] = useState('');

  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutate: deleteFile } = useDeleteFile();

  const h1Attrs = extractH1Attributes(attributes);

  const resolvedSource: H1ReportSource = h1Attrs.source ?? 'hacker-api';

  const {
    data: activities,
    isPending: activitiesLoading,
    isError: activitiesError,
    refetch: refetchActivities,
  } = useHackerOneActivities(
    h1Attrs.reportId,
    resolvedSource,
    !!h1Attrs.reportId
  );

  const { mutate: updateSeverity, isPending: isSeverityUpdating } =
    useUpdateHackerOneSeverity(h1Attrs.reportId ?? '');

  const processFiles = useCallback(
    (files: FileList) => {
      setDropZoneError('');
      Array.from(files).forEach(file => {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setDropZoneError(
            `"${file.name}" exceeds the ${formatFileSize(MAX_ATTACHMENT_BYTES)} limit.`
          );
          return;
        }

        const localId = uuidv4();
        const key = `hackerone-replies/${Date.now()}-${sanitizeFileName(file.name)}`;

        setAttachments(prev => [
          ...prev,
          {
            localId,
            name: file.name,
            size: file.size,
            status: 'uploading',
            progress: 0,
            s3Key: undefined,
            error: undefined,
          },
        ]);

        file
          .arrayBuffer()
          .then(buffer =>
            uploadFile({
              name: key,
              content: buffer,
              ignoreSnackbar: true,
              onProgress: ev => {
                setAttachments(prev =>
                  prev.map(a =>
                    a.localId === localId
                      ? { ...a, progress: Math.round(ev.percent) }
                      : a
                  )
                );
              },
            })
          )
          .then(() => {
            setAttachments(prev =>
              prev.map(a =>
                a.localId === localId
                  ? { ...a, status: 'complete', progress: 100, s3Key: key }
                  : a
              )
            );
          })
          .catch((err: unknown) => {
            const e = err as { message?: string };
            setAttachments(prev =>
              prev.map(a =>
                a.localId === localId
                  ? {
                      ...a,
                      status: 'error',
                      error: e?.message || 'Upload failed',
                    }
                  : a
              )
            );
          });
      });
    },
    [uploadFile]
  );

  const handleRemoveAttachment = useCallback(
    (localId: string) => {
      setAttachments(prev => {
        const entry = prev.find(a => a.localId === localId);
        if (entry?.status === 'complete' && entry.s3Key) {
          deleteFile({ name: entry.s3Key });
        }
        return prev.filter(a => a.localId !== localId);
      });
    },
    [deleteFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDraggingOver(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [processFiles]
  );

  const hasUploading = attachments.some(a => a.status === 'uploading');
  const hasError = attachments.some(a => a.status === 'error');

  const handleSendReply = async () => {
    if (!h1Attrs.reportId || !replyText.trim()) return;

    setIsSending(true);
    setSendError('');
    try {
      const completedKeys = attachments
        .filter(a => a.status === 'complete' && a.s3Key)
        .map(a => a.s3Key!);

      await onReply?.(
        h1Attrs.reportId,
        resolvedSource,
        replyText.trim(),
        isInternal,
        completedKeys.length > 0 ? completedKeys : undefined
      );
      setReplyText('');
      setAttachments([]);
      setIsInternal(false);
    } catch (err) {
      const e = err as { message?: string };
      setSendError(e?.message || 'Failed to send reply. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  if (!h1Attrs.reportId) {
    return (
      <div className="text-chariot-text-secondary p-4 text-sm">
        No HackerOne report found for this vulnerability.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* Report Header */}
      <div className="border-chariot-stroke rounded-lg border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <p className="text-chariot-text-secondary text-xs font-medium uppercase">
              Report ID
            </p>
            <p className="text-chariot-text-primary font-mono text-sm">
              {h1Attrs.reportId}
            </p>
          </div>

          {h1Attrs.status && (
            <span
              className={cn(
                'rounded-full px-2.5 py-0.5 text-xs font-medium',
                getStatusBadgeClass(h1Attrs.status)
              )}
            >
              {h1Attrs.status}
            </span>
          )}
        </div>

        {h1Attrs.program && (
          <div className="mt-3">
            <p className="text-chariot-text-secondary text-xs font-medium uppercase">
              Program
            </p>
            {programUrl ? (
              <a
                href={programUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-chariot-layer-primary text-sm hover:underline"
              >
                {h1Attrs.program}
              </a>
            ) : (
              <p className="text-chariot-text-primary text-sm">
                {h1Attrs.program}
              </p>
            )}
          </div>
        )}

        {resolvedSource === 'org-api' && (
          <div className="mt-3">
            <p className="text-chariot-text-secondary mb-1 text-xs font-medium uppercase">
              Update Severity
            </p>
            <div className="flex items-center gap-2">
              <Select
                value={selectedRating}
                onValueChange={value =>
                  setSelectedRating(value as H1SeverityRating)
                }
              >
                <SelectTrigger className="w-36 text-xs">
                  <SelectValue placeholder="Select rating" />
                </SelectTrigger>
                <SelectContent>
                  {(['none', 'low', 'medium', 'high', 'critical'] as const).map(
                    rating => (
                      <SelectItem
                        key={rating}
                        value={rating}
                        className="text-xs"
                      >
                        {rating.charAt(0).toUpperCase() + rating.slice(1)}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                disabled={!selectedRating || isSeverityUpdating}
                onClick={() => {
                  if (!selectedRating) return;
                  setSeverityError('');
                  updateSeverity(
                    { rating: selectedRating },
                    {
                      onError: (err: Error) => {
                        setSeverityError(
                          err?.message || 'Failed to update severity.'
                        );
                      },
                      onSuccess: () => {
                        setSelectedRating('');
                      },
                    }
                  );
                }}
              >
                {isSeverityUpdating ? 'Updating...' : 'Update'}
              </Button>
            </div>
            {severityError && (
              <p className="text-chariot-status-critical mt-1 text-xs">
                {severityError}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Activities */}
      <div className="space-y-3">
        <p className="text-chariot-text-secondary text-xs font-medium uppercase">
          Activity
        </p>

        {activitiesLoading && (
          <div className="flex items-center justify-center py-4">
            <div
              role="status"
              aria-label="Loading activities"
              className="border-chariot-layer-primary size-5 animate-spin rounded-full border-2 border-t-transparent"
            />
          </div>
        )}

        {activitiesError && !activitiesLoading && (
          <div className="space-y-2">
            <p className="text-chariot-status-critical text-sm">
              Failed to load activities.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchActivities()}
            >
              Retry
            </Button>
          </div>
        )}

        {!activitiesLoading &&
          !activitiesError &&
          activities &&
          activities.length > 0 && (
            <div className="space-y-2">
              {activities.map(activity => (
                <div
                  key={activity.id}
                  className="border-chariot-stroke bg-chariot-background-primary rounded-lg border p-3"
                >
                  <p className="text-chariot-text-secondary text-xs">
                    {formatActivityType(activity.type)}
                  </p>
                  <p className="text-chariot-text-secondary mt-1 mb-1 text-xs">
                    {new Date(activity.attributes.created_at).toLocaleString()}
                  </p>
                  <p className="text-chariot-text-primary text-sm whitespace-pre-wrap">
                    {activity.attributes.message}
                  </p>
                </div>
              ))}
            </div>
          )}

        {!activitiesLoading &&
          !activitiesError &&
          (!activities || activities.length === 0) && (
            <p className="text-chariot-text-secondary text-sm">
              No activity recorded for this report yet.
            </p>
          )}
      </div>

      {/* Reply Form */}
      {onReply && (
        <div className="space-y-3">
          <p className="text-chariot-text-secondary text-xs font-medium uppercase">
            Send Reply
          </p>

          {sendError && (
            <div
              className="bg-chariot-status-critical/10 text-chariot-status-critical rounded-md p-2 text-sm"
              role="alert"
            >
              {sendError}
            </div>
          )}

          <div className="border-chariot-stroke bg-chariot-background-primary h-[200px] rounded-lg border">
            <MarkdownEditor
              value={replyText}
              onChange={value => {
                setReplyText(value || '');
                if (sendError) setSendError('');
              }}
              preview="edit"
              className="h-full"
            />
          </div>

          {/* File attachment area — only for org-api source */}
          {resolvedSource === 'org-api' ? (
            <div className="space-y-2">
              {dropZoneError && (
                <p className="text-chariot-status-critical text-xs">
                  {dropZoneError}
                </p>
              )}

              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  'border-chariot-stroke cursor-pointer rounded-md border border-dashed p-4 text-center text-sm',
                  isDraggingOver
                    ? 'border-chariot-layer-primary bg-chariot-layer-primary/5'
                    : 'bg-chariot-background-primary hover:bg-chariot-background-secondary'
                )}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={e => {
                    if (e.target.files && e.target.files.length > 0) {
                      processFiles(e.target.files);
                    }
                    e.target.value = '';
                  }}
                />
                <p className="text-chariot-text-secondary">
                  Drop files or click to select. Max{' '}
                  {formatFileSize(MAX_ATTACHMENT_BYTES)} per file.
                </p>
              </div>

              {attachments.length > 0 && (
                <ul className="space-y-2">
                  {attachments.map(attachment => (
                    <li
                      key={attachment.localId}
                      className="border-chariot-stroke bg-chariot-background-primary rounded-md border p-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-chariot-text-primary truncate text-sm">
                            {attachment.name}
                          </p>
                          <p className="text-chariot-text-secondary text-xs">
                            {formatFileSize(attachment.size)}
                          </p>

                          {attachment.status === 'uploading' && (
                            <div className="mt-1">
                              <div className="bg-chariot-stroke h-1 w-full overflow-hidden rounded-full">
                                <div
                                  className="bg-chariot-layer-primary h-full rounded-full transition-all duration-200"
                                  style={{ width: `${attachment.progress}%` }}
                                />
                              </div>
                              <p className="text-chariot-text-secondary mt-0.5 text-xs">
                                Uploading {attachment.progress}%
                              </p>
                            </div>
                          )}

                          {attachment.status === 'complete' && (
                            <p className="text-chariot-status-positive mt-0.5 text-xs">
                              Uploaded
                            </p>
                          )}

                          {attachment.status === 'error' && (
                            <p className="text-chariot-status-critical mt-0.5 text-xs">
                              {attachment.error ?? 'Upload failed'}
                            </p>
                          )}
                        </div>

                        <button
                          type="button"
                          aria-label={`Remove ${attachment.name}`}
                          onClick={() =>
                            handleRemoveAttachment(attachment.localId)
                          }
                          className="text-chariot-text-secondary hover:text-chariot-text-primary flex-shrink-0 text-sm leading-none"
                        >
                          &times;
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-chariot-text-secondary text-xs">
              File attachments are only supported on reports synced from a
              HackerOne Organization API integration.
            </p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                id="h1-internal-toggle"
                checked={isInternal}
                onCheckedChange={setIsInternal}
              />
              <Label
                htmlFor="h1-internal-toggle"
                className="text-chariot-text-secondary cursor-pointer text-xs"
              >
                Internal comment
              </Label>
            </div>
            <Button
              size="sm"
              onClick={handleSendReply}
              disabled={
                !replyText.trim() ||
                isSending ||
                !h1Attrs.reportId ||
                hasUploading ||
                hasError
              }
            >
              {isSending ? 'Sending...' : 'Send Reply'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
